import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { can, useSession } from '../lib/session';
import { useApiError } from '../lib/useApiError';
import { Sheet } from './Sheet';
import { Alert, Button } from './ui';

/**
 * The owner's "delete for good", for an order, a car, a booking, a service or
 * a reward. Shown to nobody else; asks once, naming what goes.
 */
export function EraseButton({ path, name, details, onDone, className = '', compact = false }: {
  /** The API path of the thing, e.g. `orders/<id>`; `/forever` is added. */
  path: string;
  /** What is being deleted, as the person knows it: a reference, a plate, a name. */
  name: string;
  /** Anything worth knowing before saying yes, rendered when the sheet opens. */
  details?: (open: boolean) => ReactNode;
  onDone?: () => void;
  className?: string;
  compact?: boolean;
}) {
  const { t } = useI18n();
  const { data: session } = useSession();
  const errorText = useApiError();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const erase = useMutation({
    mutationFn: () => api<unknown>(`/admin/${path}/forever`, { method: 'DELETE' }),
    onSuccess: async () => {
      setOpen(false);
      onDone?.();
      // Rare and far-reaching: everything that might have shown it is refreshed.
      await queryClient.invalidateQueries();
    },
  });

  if (!can(session, 'data.delete')) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => { erase.reset(); setOpen(true); }}
        className={`${compact ? 'min-h-9 px-2 text-xs' : 'min-h-11 px-3 text-sm'} rounded-xl font-semibold text-danger transition hover:bg-danger/10 ${className}`}
      >
        {t('erase.action')}
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title={t('erase.question')}>
        <div className="space-y-3">
          <p className="text-sm font-semibold text-ink">{name}</p>
          {details?.(open)}
          {erase.error instanceof ApiError && <Alert>{errorText(erase.error) ?? erase.error.message}</Alert>}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button variant="ghost" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="danger" loading={erase.isPending} onClick={() => erase.mutate()}>{t('erase.confirm')}</Button>
          </div>
        </div>
      </Sheet>
    </>
  );
}
